/**
 * 三役の街 共通UI（salon / tokiwa / stage の3ページで共有）
 *
 * anokoe-design 調停原則: 事実性①>a11y②>タスク③>演出④>美観⑤。
 * - 役割色は識別専用（文字はダーク変種）、4状態、44pxタップ、reduced-motion代替、YES/NO禁止。
 * - Pop/Light v2 トークン（.btn / .pop-card / role-* 色）を用いる。
 */
import { FC, ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { api } from '../../services/api';
import { LoginModal } from '../LoginModal';
import { useGamenics, GamenicsEvent } from '../../hooks/useGamenics';

export type TownRole = 'script' | 'illustration' | 'voice';

/** 街区の識別情報（役割色は識別専用・文字はダーク変種／tokens.md） */
export const TOWN_ROLE = {
  script: { noun: '物語', label: '書き手', accent: 'role-script', accentText: 'role-script-text', soft: 'role-script-soft' },
  illustration: { noun: '絵', label: '描き手', accent: 'role-illustration', accentText: 'role-illustration-text', soft: 'role-illustration-soft' },
  voice: { noun: '声', label: '演じ手', accent: 'role-voice', accentText: 'role-voice-text', soft: 'role-voice-soft' },
} as const;

/** ユーザー取得＋ログイン誘導（ページローカルで LoginModal を出す） */
export const useTownUser = () => {
  const [user, setUser] = useState<{ id: string; name?: string; displayName?: string } | null>(null);
  const [ready, setReady] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getUserProfile().then(r => {
      if (cancelled) return;
      if (r.success && r.data?.user) setUser(r.data.user);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  const loginGate: ReactNode = showLogin ? (
    <LoginModal onClose={() => setShowLogin(false)} onLoginSuccess={(u) => { setUser(u); setShowLogin(false); }} />
  ) : null;

  /** ログイン必須アクションのラッパ: 未ログインなら誘導、ログイン済みなら実行 */
  const requireLogin = (fn: () => void) => { if (user) fn(); else setShowLogin(true); };

  return { user, ready, requireLogin, loginGate, promptLogin: () => setShowLogin(true) };
};

/** 街区ページの器（ヘッダー: ホームへ戻る＋タイトル＋街区色のキッカー） */
const DOT_CLS: Record<TownRole, string> = { script: 'bg-role-script', illustration: 'bg-role-illustration', voice: 'bg-role-voice' };
const KICKER_CLS: Record<TownRole, string> = { script: 'text-role-script-text', illustration: 'text-role-illustration-text', voice: 'text-role-voice-text' };

export const TownShell: FC<{ role: TownRole; kicker: string; title: string; children: ReactNode; actions?: ReactNode }> = ({ role, kicker, title, children, actions }) => {
  const [, setLocation] = useLocation();
  const { playSound } = useGamenics();
  return (
    <div className="w-full max-w-[1120px] mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); setLocation('/'); }}
          className="btn btn-ghost btn-sm min-h-[44px]"
          aria-label="ホームへ戻る"
        >
          <span aria-hidden>←</span> ちょっと戻る
        </button>
        <div className="flex-1 min-w-[200px]">
          <span className={`block text-[11px] font-display font-bold tracking-[0.15em] uppercase ${KICKER_CLS[role]}`}>{kicker}</span>
          <h1 className="font-display font-black text-ink text-[clamp(24px,3vw,31px)] flex items-center gap-3">
            {title}
            <span className={`inline-block w-3 h-3 rounded-full ${DOT_CLS[role]} border-2 border-ink`} aria-hidden />
          </h1>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
};

/** ローディング（reduced-motion時はシマー無しの静的プレースホルダ／Tailwind motion-reduce） */
export const TownLoading: FC<{ label?: string }> = ({ label = '読み込んでいます' }) => (
  <div className="py-16 flex flex-col items-center gap-3 text-ink-2" role="status" aria-live="polite">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full motion-safe:animate-spin motion-reduce:animate-none" />
    <span className="text-sm">{label}</span>
  </div>
);

/** エラー（ホーム・既存機能は無傷。ページ内で再試行） */
export const TownError: FC<{ message?: string; onRetry: () => void }> = ({ message = 'うまく開けませんでした', onRetry }) => {
  const { playSound } = useGamenics();
  return (
    <div className="py-16 flex flex-col items-center gap-4 text-center" role="alert">
      <span className="text-ink-2">{message}</span>
      <button className="btn btn-primary min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); onRetry(); }}>
        もう一度ためす
      </button>
    </div>
  );
};

/** 空状態（招待として見せる。偽の件数・ダミーは出さない＝事実性①） */
export const TownEmpty: FC<{ title: string; children?: ReactNode }> = ({ title, children }) => (
  <div className="py-16 flex flex-col items-center gap-4 text-center">
    <p className="text-ink-2 max-w-md">{title}</p>
    {children}
  </div>
);

/** 街区目標の提示（決定13・各街区最低1種）。実データのみ・0件は非表示（偽の目標を出さない） */
export const TownGoals: FC<{ role: TownRole }> = ({ role }) => {
  const [goals, setGoals] = useState<any[]>([]);
  const key = role === 'script' ? 'salon' : role === 'illustration' ? 'tokiwa' : 'stage';
  useEffect(() => {
    api.getUserGoals().then(r => {
      const all = r.data || [];
      setGoals(all.filter((g: any) => String(g.goal_type || '').includes(key) || new RegExp(key === 'salon' ? 'サロン|ノート' : key === 'tokiwa' ? '荘|スケッチ|デッサン|お題' : 'ステージ|台本|声|一座').test(String(g.display_text || ''))));
    });
  }, [key]);
  if (goals.length === 0) return null;
  // 静的クラス（Tailwind JIT が拾えるよう動的結合しない）
  const chipCls: Record<TownRole, string> = {
    script: 'bg-role-script-soft text-role-script-text border-role-script',
    illustration: 'bg-role-illustration-soft text-role-illustration-text border-role-illustration',
    voice: 'bg-role-voice-soft text-role-voice-text border-role-voice',
  };
  return (
    <div className="mb-6">
      <h3 className="text-xs font-bold text-ink-2 mb-2 tracking-wide">この街区の目標</h3>
      <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
        {goals.slice(0, 3).map((g, i) => (
          <li key={i} className={`text-xs px-3 py-1.5 rounded-full border ${chipCls[role]}`}>
            {g.display_text || g.label || '目標'}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** 相対時刻（実データのみ。桁ブレ防止に tabular-nums はCSSで） */
export const timeAgo = (unixSec: number): string => {
  const diff = Math.floor(Date.now() / 1000) - unixSec;
  if (diff < 60) return 'たった今';
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  return `${Math.floor(diff / 86400)}日前`;
};
